import { Box } from '@mui/material';
import { useDeleteAdmin } from '_api';
import { AccountType, Therapist } from '_api/types';
import { ACCOUNT_TYPE, AccountTypeLookUp, UsersRouteMap } from '_constants';
import { ActionsFlexBox } from 'components/molecules';
import { ViewButtons } from 'components/molecules/types';
import { TableHeader } from 'components/organisms';
import { AddUser } from 'components/organisms/modals';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigateTo } from 'utils';
import { useTherapists, useSearchResults, useUser } from 'utils/hooks';

export const UserManagement = () => {
  const { existingSearchTerm, updateSearchTerm } = useSearchResults();
  const navigate = useNavigateTo();
  const [openModal, setModalOpen] = useState(false);
  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);
  const { allActiveTherapistAndAdmin, deleteTherapist, isLoading } = useTherapists();
  const { account } = useUser();
  const { mutateAsync: deleteAdmin } = useDeleteAdmin();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState<Therapist[]>([]);

  useEffect(() => setSearchTerm(existingSearchTerm || ''), [existingSearchTerm]);

  useMemo(() => {
    if (searchTerm === '') {
      setFilteredData(allActiveTherapistAndAdmin);
    } else if (allActiveTherapistAndAdmin) {
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      const filtered = allActiveTherapistAndAdmin.filter((therapist) => {
        const fullName = `${therapist.firstName} ${therapist.lastName}`.toLowerCase();
        const hospital = `${therapist.hospital}`?.toLowerCase();
        const email = `${therapist.email}`?.toLowerCase();
        return (
          fullName.includes(lowercasedSearchTerm) ||
          therapist.email.toLowerCase().includes(lowercasedSearchTerm) ||
          hospital.includes(lowercasedSearchTerm) ||
          email?.includes(lowercasedSearchTerm)
        );
      });
      setFilteredData(filtered);
    }
  }, [searchTerm, allActiveTherapistAndAdmin]);

  const handleDeleteOnClick = (id: number, accountType: AccountType) => {
    if (accountType === ACCOUNT_TYPE.therapist) {
      deleteTherapist({ therapistId: id });
    } else {
      deleteAdmin({ adminId: id });
    }
  };

  const actionButtons: ViewButtons = {
    editButton: true,
    deleteButton: true,
  };

  const handleEditOnClick = (id: number) => {
    navigate(UsersRouteMap.DETAIL, id.toString());
  };

  const disableButtons = (row: Therapist) => {
    if (account && account.accountType == ACCOUNT_TYPE.ADMIN) {
      if (row.accountType === ACCOUNT_TYPE.ADMIN) {
        if (account.id == row.id) {
          return true;
        }
        return false;
      }
      return false;
    }

    if (account && account.accountType == ACCOUNT_TYPE.ORG_ADMIN) {
      if (
        row.accountType == ACCOUNT_TYPE.ADMIN ||
        row.accountType == ACCOUNT_TYPE.ORG_ADMIN
      ) {
        return true;
      }
      return false;
    }

    if (account && account.accountType == ACCOUNT_TYPE.HOS_ADMIN) {
      if (row.accountType == ACCOUNT_TYPE.THERAPIST) {
        return false;
      }
      return true;
    }

    return true;
  };

  const userTableColumns = useMemo<MRT_ColumnDef<Therapist>[]>(
    () => [
      {
        accessorFn: (row) => `${row.firstName} ${row.lastName}`,
        header: 'Name',
      },
      {
        accessorFn: (row) => row.email,
        header: 'Email',
      },
      {
        accessorFn: (row) =>
          row && row.hospital && row.hospital.name ? row.hospital.name : '',
        header: 'Hospital',
      },
      {
        accessorFn: (row) => {
          const lookUp = AccountTypeLookUp.find((item) => item.type === row.accountType);
          return lookUp ? lookUp.value : '';
        },
        header: 'Role',
      },
      {
        header: 'Actions',
        enableSorting: false,
        accessorFn: (row) => (
          <ActionsFlexBox
            msg={'user'}
            disableEdit={disableButtons(row)}
            disableDelete={disableButtons(row)}
            editOnClick={() => handleEditOnClick(Number(row.id))}
            deleteOnClick={() => handleDeleteOnClick(Number(row.id), row.accountType)}
            actionButtons={actionButtons}
          />
        ),
      },
    ],
    [account],
  );

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    updateSearchTerm(searchTerm);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <TableHeader
          onAddClick={handleOpen}
          handleSearch={handleSearch}
          value={searchTerm}
        />
      </Box>
      <Box sx={{ marginTop: 10 }}>
        {filteredData && !isLoading ? (
          <MaterialReactTable
            columns={userTableColumns}
            data={filteredData}
            enableSorting={true}
            enableColumnActions={false}
            enableTopToolbar={false}
            enablePagination={true}
          />
        ) : null}
      </Box>
      <AddUser open={openModal} onClose={handleClose} />
    </Box>
  );
};
